import { useState, useEffect, useMemo } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { SliderContainer, SliderLabel, StyledSlider, ValueDisplay } from './styles'
import {
  Container,
  Card,
  Select,
  PageContainer,
  PageHeader,
  InfoCard,
  Badge,
  BackButton,
  Result,
  ButtonDiv,
  InputDiv,
} from '../../../styles/global'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'

export function PlumbingWaterTank() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const buildingTypes = useMemo(
    () => [
      { type: 'Alojamentos provisórios', consume: 80, unit: 'pessoas' },
      { type: 'Casas populares ou rurais', consume: 120, unit: 'pessoas' },
      { type: 'Residências', consume: 150, unit: 'pessoas' },
      { type: 'Apartamentos', consume: 200, unit: 'pessoas' },
      { type: 'Hotéis (s/cozinha e s/lavanderia', consume: 120, unit: 'hóspedes' },
      { type: 'Escolas (internatos)', consume: 150, unit: 'pessoas' },
      { type: 'Escolas (semi internatos)', consume: 100, unit: 'pessoas' },
      { type: 'Escolas (externatos)', consume: 50, unit: 'pessoas' },
      { type: 'Quartéis', consume: 150, unit: 'pessoas' },
      { type: 'Edifícios públicos e comerciais', consume: 50, unit: 'pessoas' },
      { type: 'Escritórios', consume: 50, unit: 'pessoas' },
      { type: 'Cinemas e teatros', consume: 2, unit: 'lugares' },
      { type: 'Cinemas e teatros', consume: 2, unit: 'lugares' },
      { type: 'Templos', consume: 2, unit: 'lugares' },
      { type: 'Restaurantes e similares', consume: 25, unit: 'refeições' },
      { type: 'Garagens', consume: 50, unit: 'automóveis' },
      { type: 'Personalizado', consume: 0, unit: 'unidades' },
    ],
    []
  )

  const [unit, setUnit] = useState('pessoas')
  const [quantity, setQuantity] = useState('')
  const [selectedType, setSelectedType] = useState('Alojamentos provisórios')
  const [days, setDays] = useState(1.5)
  const [consume, setConsume] = useState(80)
  const [fireReserve, setFireReserve] = useState(0)
  const [upperTankValue, setUpperTankValue] = useState(100)
  const [result, setResult] = useState('')
  const [isInputDisabled, setIsInputDisabled] = useState(true)
  const [isFormValid, setIsFormValid] = useState(false)

  const handleTypeChange = (event) => {
    const selectedType = event.target.value
    if (selectedType == 'Personalizado') setIsInputDisabled(false)
    else {
      setIsInputDisabled(true)
      setConsume(buildingTypes.find((b) => b.type == selectedType).consume)
    }

    setUnit(buildingTypes.find((b) => b.type == selectedType).unit)
    setSelectedType(selectedType)
  }

  useEffect(() => {
    if (quantity === '' || quantity === 0) {
      setIsFormValid(false)
    } else if (consume === '' || consume === 0) {
      setIsFormValid(false)
    } else setIsFormValid(true)
  }, [consume, quantity, isFormValid])

  async function handleCalculate(event) {
    event.preventDefault()

    try {
      const data = {
        consume,
        quantity,
        days,
        fireReserve,
        upperTankValue,
      }

      const response = await api.post('/plumbing/water-tank', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    }
  }

  const headerGradient = 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'

  return (
    <PageContainer>
      <PageHeader $gradient={headerGradient}>
        <div className="header-content">
          <BackButton to="/hidrossanitario">
            <FiArrowLeft /> Voltar para Hidrossanitário
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/hidrossanitario">Hidrossanitário</a>
            <span>/</span>
            <span>Reservatório</span>
          </div>
          <h1>Dimensionamento de reservatório</h1>
          <p className="subtitle">
            Calcule o volume necessário considerando consumo diário, tempo de autonomia e reserva de incêndio.
          </p>
          <div className="badges">
            <Badge>NBR 5626</Badge>
            <Badge>Autonomia</Badge>
            <Badge>Reserva de incêndio</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)"
        $border="2px solid #06b6d4"
        $primaryColor="#0e7490"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Escolha o tipo de edificação para carregar consumos típicos, ajuste dias de autonomia e distribua o volume
              entre reservatórios superior e inferior.
            </p>
            <p>
              Inclua a reserva de incêndio quando aplicável e visualize a partição do volume resultante para cada
              reservatório.
            </p>
            <div className="norms">
              <span>NBR 5626</span>
              <span>Sistemas prediais de água fria</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Reservatório</h2>
          <h3>Dados de uso</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Tipo de edificação</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="buldingTypeSelect"
                  value={selectedType}
                  onChange={handleTypeChange}
                >
                  {buildingTypes.map((b, index) => (
                    <option key={index} value={b.type}>
                      {b.type}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Consumo por dia (L/d)</span>
              <input
                type="number"
                value={consume || ''}
                disabled={isInputDisabled}
                onChange={(event) => setConsume(Number(event.target.value))}
              />
            </InputDiv>
            <InputDiv>
              <span>Quantidade de {unit}</span>
              <input
                type="number"
                value={quantity || ''}
                onChange={(event) => setQuantity(Number(event.target.value))}
              />
            </InputDiv>
            <InputDiv>
              <span>Dias</span>
              <input type="number" value={days || ''} onChange={(event) => setDays(Number(event.target.value))} />
            </InputDiv>
            <h3>Distribuição e Reserva de Incêndio</h3>
            <InputDiv>
              <span>Reserva de Incêndio</span>
              <input
                type="number"
                value={fireReserve}
                onChange={(event) => setFireReserve(Number(event.target.value))}
              />
            </InputDiv>
            <InputDiv>
              <span>Distribuição dos Reservatórios</span>
              <SliderContainer>
                <SliderLabel>Parte no reservatório superior (%)</SliderLabel>
                <StyledSlider
                  type="range"
                  min="40"
                  max="100"
                  step="5"
                  value={upperTankValue}
                  onChange={(e) => setUpperTankValue(e.target.value)}
                />
                <ValueDisplay>{upperTankValue}%</ValueDisplay>
              </SliderContainer>
            </InputDiv>
            <div></div>
            <ButtonDiv $isFormValid={isFormValid}>
              <button type="submit" disabled={!isFormValid || !canCalculate}>
                {!canCalculate ? 'Limite atingido' : 'Calcular'}
              </button>
            </ButtonDiv>
          </form>
        </Card>
        {result !== '' && (
          <Result>
            <h2>Resultado</h2>
            <p>Volume para consumo: {result.useVolume.toLocaleString('pt-BR')} L</p>
            {result.totalVolume != result.useVolume && (
              <p>Volume total: {result.totalVolume.toLocaleString('pt-BR')} L</p>
            )}
            {result.lowerTank != 0 && (
              <>
                <h3>Divisão</h3>
                <p>Volume no reservatório superior: {result.upperTank.toLocaleString('pt-BR')} L</p>
                <p>Volume no reservatório inferior: {result.lowerTank.toLocaleString('pt-BR')} L</p>
              </>
            )}
          </Result>
        )}
      </Container>
    </PageContainer>
  )
}
